/**
 * DataListRoot -- root component and compound component assembly point.
 *
 * Supports two configuration modes:
 * - **Local mode**: `config` prop provides DataListConfig directly
 * - **Remote mode**: `remoteConfigId` prop fetches config from Origami API
 *
 * Also supports controlled mode when an external `list` (UseDataListReturn)
 * is provided, bypassing the internal useDataList hook.
 *
 * Follows the same compound component pattern as EntityFormRoot:
 * `Object.assign(DataListRoot, { Sub1, Sub2, ... })`.
 *
 * @see Plan sections 4.10 and 4.11
 * @see types.ts `DataListProps` for the full props interface.
 */

import * as React from 'react';
import { DataListContext } from '../context/DataListContext';
import { useDataList } from '../hooks/useDataList';
import { useRemoteComponentConfig } from '../../shared';
import { Spinner, Alert } from '@components/base/feedback';
import { DataListHeader } from './DataListHeader';
import { DataListToolbar } from './DataListToolbar';
import { DataListTable } from './DataListTable';
import { DataListFooter } from './DataListFooter';
import { DataListBulkActions } from './DataListBulkActions';
import { DataListEmpty } from './DataListEmpty';
import { DataListAuto } from './DataListAuto';
import type { DataListConfig, DataListProps, UseDataListReturn } from '../types';

// ---------------------------------------------------------------------------
// Props for the inner component (config is required)
// ---------------------------------------------------------------------------

interface DataListLocalProps {
  config: DataListConfig;
  list?: UseDataListReturn;
  onDataLoaded?: (data: Record<string, unknown>[]) => void;
  onError?: (error: unknown) => void;
  className?: string;
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// DataListLocal -- core list with guaranteed config
// ---------------------------------------------------------------------------

function DataListLocal({
  config,
  list: externalList,
  onDataLoaded,
  onError,
  className,
  children,
}: Readonly<DataListLocalProps>): React.ReactElement {
  // If external list provided (controlled mode), use it; otherwise create internal
  const internalList = useDataList(config);
  const list = externalList ?? internalList;

  // Fire onDataLoaded when data changes
  const prevDataRef = React.useRef<Record<string, unknown>[] | undefined>(undefined);
  React.useEffect(() => {
    if (
      onDataLoaded &&
      list.data !== prevDataRef.current &&
      list.data.length > 0 &&
      !list.isLoading
    ) {
      prevDataRef.current = list.data;
      onDataLoaded(list.data);
    }
  }, [list.data, list.isLoading, onDataLoaded]);

  // Fire onError when an error occurs
  const prevErrorRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (onError && list.error && list.error !== prevErrorRef.current) {
      prevErrorRef.current = list.error;
      onError(list.error);
    }
  }, [list.error, onError]);

  const contextValue = React.useMemo(
    () => ({ config, list }),
    [config, list],
  );

  const containerClasses =
    ['flex flex-col gap-3', config.cssOverrides?.root, className].filter(Boolean).join(' ');

  return (
    <DataListContext.Provider value={contextValue}>
      <div
        className={containerClasses}
        data-testid="data-list-root"
      >
        {children}
      </div>
    </DataListContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// DataListRemote -- fetches config, then renders local
// ---------------------------------------------------------------------------

function DataListRemote({
  remoteConfigId,
  tenant,
  module,
  loadingFallback,
  errorMessage,
  ...rest
}: Readonly<DataListProps>): React.ReactElement {
  const { config, isLoading, error } = useRemoteComponentConfig<DataListConfig>({
    componentId: remoteConfigId!,
    tenant,
    module,
    errorMessage,
  });

  if (isLoading) {
    return <>{loadingFallback ?? <Spinner />}</>;
  }

  if (error || !config) {
    return (
      <Alert variant="error">
        {error ?? 'Erreur de configuration du composant'}
      </Alert>
    );
  }

  return <DataListLocal {...rest} config={config} />;
}

// ---------------------------------------------------------------------------
// DataListRoot -- mode branching
// ---------------------------------------------------------------------------

function DataListRoot(props: Readonly<DataListProps>): React.ReactElement {
  const { config, remoteConfigId } = props;

  if (config && remoteConfigId) {
    throw new Error(
      'DataList: "config" and "remoteConfigId" are mutually exclusive. Provide one or the other, not both.',
    );
  }

  if (!config && !remoteConfigId) {
    throw new Error(
      'DataList: either "config" or "remoteConfigId" must be provided.',
    );
  }

  if (remoteConfigId) {
    return <DataListRemote {...props} />;
  }

  // Local mode -- config is guaranteed here
  return (
    <DataListLocal
      config={config!}
      list={props.list}
      onDataLoaded={props.onDataLoaded}
      onError={props.onError}
      className={props.className}
    >
      {props.children}
    </DataListLocal>
  );
}

DataListRoot.displayName = 'DataListRoot';

// ---------------------------------------------------------------------------
// Compound Component Assembly
// ---------------------------------------------------------------------------

/**
 * DataList compound component.
 *
 * Usage:
 * - DataList.Header     - Title, icon, and collapse toggle
 * - DataList.Toolbar    - Search, filters, create, export
 * - DataList.Table      - Data table with sorting, selection, actions
 * - DataList.Footer     - Pagination / load-more
 * - DataList.BulkActions - Bulk action bar
 * - DataList.Empty      - Empty state display
 * - DataList.Auto       - Auto-renders all sub-components from config
 */
const DataList = Object.assign(DataListRoot, {
  Header: DataListHeader,
  Toolbar: DataListToolbar,
  Table: DataListTable,
  Footer: DataListFooter,
  BulkActions: DataListBulkActions,
  Empty: DataListEmpty,
  Auto: DataListAuto,
});

export { DataList };
